// frontend/src/components/client/ClientCalendar.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock, Plane, Wrench, Fuel, Users, CheckCircle, XCircle, X } from 'lucide-react';
import { getMyEvents } from '../../services/clientApi';

const EVENT_ICONS = {
  flight: { icon: Plane, color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  fuel: { icon: Fuel, color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
  maintenance: { icon: Wrench, color: 'text-red-400', bgColor: 'bg-red-500/20' },
  meeting: { icon: Users, color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
  appointment: { icon: Calendar, color: 'text-green-400', bgColor: 'bg-green-500/20' }
};

const StatusBadge = ({ status }) => {
  const config = {
    scheduled: { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400' },
    completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: XCircle },
    in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400' }
  };
  const { label, color } = config[status] || config.scheduled;
  return <span className={`px-2 py-0.5 rounded-full text-xs ${color}`}>{label}</span>;
};

const ClientCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const startDate = getStartDate();
      const endDate = getEndDate();
      const response = await getMyEvents(startDate, endDate);
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStartDate = () => {
    const date = new Date(currentDate);
    if (viewMode === 'month') {
      date.setDate(1);
    } else if (viewMode === 'week') {
      const day = date.getDay();
      date.setDate(date.getDate() - day);
    }
    return date.toISOString().split('T')[0];
  };

  const getEndDate = () => {
    const date = new Date(currentDate);
    if (viewMode === 'month') {
      date.setMonth(date.getMonth() + 1);
      date.setDate(0);
    } else if (viewMode === 'week') {
      const day = date.getDay();
      date.setDate(date.getDate() + (6 - day));
    }
    return date.toISOString().split('T')[0];
  };

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') newDate.setMonth(newDate.getMonth() - 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    setSelectedEvent(null);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') newDate.setMonth(newDate.getMonth() + 1);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    setSelectedEvent(null);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedEvent(null);
  };

  const formatMonthHeader = () => {
    return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatWeekHeader = () => {
    const start = new Date(currentDate);
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === day.toDateString();
    });
  };

  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    const startDayOfWeek = startDate.getDay();
    startDate.setDate(startDate.getDate() - startDayOfWeek);
    
    const weeks = [];
    let current = new Date(startDate);
    
    while (current <= lastDay) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs text-gray-500 py-2">{day}</div>
            ))}
          </div>
          {weeks.map((week, idx) => (
            <div key={idx} className="grid grid-cols-7 gap-1 mb-1">
              {week.map((day, dayIdx) => {
                const isCurrentMonth = day.getMonth() === month;
                const dayEvents = getEventsForDay(day);
                const isToday = day.toDateString() === new Date().toDateString();
                
                return (
                  <div
                    key={dayIdx}
                    className={`min-h-[100px] p-1 rounded-lg border ${isCurrentMonth ? 'border-gray-700' : 'border-gray-800 bg-gray-900/30'} ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                  >
                    <div className={`text-right text-sm p-1 ${isCurrentMonth ? 'text-white' : 'text-gray-500'}`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map(event => {
                        const config = EVENT_ICONS[event.event_type] || EVENT_ICONS.appointment;
                        const Icon = config.icon;
                        return (
                          <div
                            key={event.id}
                            onClick={() => setSelectedEvent(event)}
                            className={`text-xs p-1 rounded cursor-pointer truncate ${config.bgColor}`}
                          >
                            <div className="flex items-center gap-1">
                              <Icon className={`w-2.5 h-2.5 ${config.color}`} />
                              <span className="text-white truncate">{event.title}</span>
                            </div>
                          </div>
                        );
                      })}
                      {dayEvents.length > 2 && (
                        <div className="text-xs text-gray-500 text-center">
                          +{dayEvents.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const startDate = new Date(currentDate);
    const day = startDate.getDay();
    startDate.setDate(startDate.getDate() - day);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }

    const hours = Array.from({ length: 12 }, (_, i) => i + 6); // 6 AM to 6 PM

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          <div className="grid grid-cols-8 gap-1 mb-2">
            <div className="text-xs text-gray-500 py-2"></div>
            {days.map((day, idx) => (
              <div key={idx} className="text-center">
                <div className="text-xs text-gray-500">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div className={`text-sm font-medium ${day.toDateString() === new Date().toDateString() ? 'text-blue-400' : 'text-white'}`}>
                  {day.getDate()}
                </div>
              </div>
            ))}
          </div>
          {hours.map(hour => (
            <div key={hour} className="grid grid-cols-8 gap-1 mb-1 min-h-[60px]">
              <div className="text-xs text-gray-500 py-2">{hour}:00</div>
              {days.map((day, dayIdx) => {
                const hourEvents = events.filter(event => {
                  const eventHour = new Date(event.start_time).getHours();
                  return eventHour === hour && new Date(event.start_time).toDateString() === day.toDateString();
                });
                return (
                  <div key={dayIdx} className="border border-gray-700 rounded-lg p-1 min-h-[60px]">
                    {hourEvents.map(event => {
                      const config = EVENT_ICONS[event.event_type] || EVENT_ICONS.appointment;
                      const Icon = config.icon;
                      return (
                        <div
                          key={event.id}
                          onClick={() => setSelectedEvent(event)}
                          className={`text-xs p-1 rounded cursor-pointer mb-1 ${config.bgColor}`}
                        >
                          <div className="flex items-center gap-1">
                            <Icon className={`w-2.5 h-2.5 ${config.color}`} />
                            <span className="text-white truncate">{event.title}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDay(currentDate);
    dayEvents.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    return (
      <div className="space-y-3">
        {dayEvents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No events scheduled for this day</p>
          </div>
        ) : (
          dayEvents.map(event => {
            const config = EVENT_ICONS[event.event_type] || EVENT_ICONS.appointment;
            const Icon = config.icon;
            return (
              <div
                key={event.id}
                onClick={() => setSelectedEvent(event)}
                className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 cursor-pointer hover:bg-gray-800/70 transition"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.bgColor}`}>
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-white font-semibold">{event.title}</h3>
                      <StatusBadge status={event.status} />
                    </div>
                    <p className="text-sm text-gray-400 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {formatTime(event.start_time)} - {formatTime(event.end_time)}
                    </p>
                    {event.tail_number && (
                      <p className="text-xs text-gray-500 mt-1">{event.tail_number}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // Event Detail Modal
  const EventDetailModal = ({ event, onClose }) => {
    if (!event) return null;
    const config = EVENT_ICONS[event.event_type] || EVENT_ICONS.appointment;
    const Icon = config.icon;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${config.bgColor}`}>
                <Icon className={`w-5 h-5 ${config.color}`} />
              </div>
              <h3 className="text-white font-semibold">{event.title}</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Clock className="w-4 h-4" />
              <span>
                {new Date(event.start_time).toLocaleString()} - {new Date(event.end_time).toLocaleTimeString()}
              </span>
            </div>
            {event.description && (
              <div className="pt-2 border-t border-gray-700">
                <p className="text-gray-400 text-sm">{event.description}</p>
              </div>
            )}
            {event.tail_number && (
              <div className="pt-2 border-t border-gray-700">
                <p className="text-xs text-gray-500">Aircraft</p>
                <p className="text-white text-sm">{event.tail_number}</p>
              </div>
            )}
            <div className="pt-2 border-t border-gray-700">
              <p className="text-xs text-gray-500">Status</p>
              <StatusBadge status={event.status} />
            </div>
          </div>
          <div className="p-4 border-t border-gray-700">
            <button onClick={onClose} className="w-full py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">My Calendar</h2>
          <p className="text-gray-400 text-sm">View your scheduled flights and appointments</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-700 rounded-lg p-0.5">
            {['day', 'week', 'month'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 text-sm rounded-md transition ${
                  viewMode === mode ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={handlePrev} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={handleToday} className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-white">
            Today
          </button>
          <button onClick={handleNext} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <h3 className="text-lg font-semibold text-white">
          {viewMode === 'month' ? formatMonthHeader() : viewMode === 'week' ? formatWeekHeader() : currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </h3>
        <div className="w-20" /> {/* Spacer for alignment */}
      </div>

      {/* Calendar Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'day' && renderDayView()}
        </>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
};

export default ClientCalendar;